import { ReactElement, ReactNode } from "jsx-dom";
import { i18n } from "../../i18n";
import { Icon } from "../Icon";
import { IconType } from "../../../types";
import { ReadMoreList, ReadMoreListDevice } from "../../read-more-list";

const grades = ["fair", "good", "excellent", "premium"] as const;
type Grade = (typeof grades)[number];

const readMoreListClass = (grade: Grade) => `js-refurb-read-more-list-${grade}`;
const listItemClass = (grade: Grade) => `refurb-list-item-${grade}`;

export function initRefurbishedReadMoreLists() {
  grades.forEach((grade) => {
    const readMoreList = new ReadMoreList(
      `.${readMoreListClass(grade)}`,
      ReadMoreListDevice.All,
      false
    );
    readMoreList.visibleItems = 3;
    readMoreList.init();
  });
}

export const RefurbishedInfoModalContent = () => {
  return (
    <>
      <main className="tw:px-4">
        <h4>{i18n("product.refurbished-link")}</h4>
        <hr className="tw:block tw:border-0 tw:border-b tw:border-fruugo-border-control tw:w-full tw:mb-4"></hr>
        <h4>{i18n("product.refurbished.modal.title")}</h4>
        <p>{i18n("product.refurbished.modal.description")}</p>

        <section class="tw:pb-4 tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:lg:grid-cols-4 tw:gap-4">
          <RefurbishedGradeCard
            name={i18n("product.refurbished.fair")}
            content={<FairGradeContent />}
            imageUrl="/marketplace/images/refurbished/FairGrade.webp"
            iconName="sunny"
          />
          <RefurbishedGradeCard
            name={i18n("product.refurbished.good")}
            content={<GoodGradeContent />}
            imageUrl="/marketplace/images/refurbished/GoodGrade.webp"
            iconName="flare"
          />
          <RefurbishedGradeCard
            name={i18n("product.refurbished.excellent")}
            content={<ExellentGradeContent />}
            imageUrl="/marketplace/images/refurbished/ExcellentGrade.webp"
            iconName="diamond-shine"
          />
          <RefurbishedGradeCard
            name={i18n("product.refurbished.premium")}
            content={<PremiumGradeContent />}
            imageUrl="/marketplace/images/refurbished/PremiumGrade.webp"
            iconName="trophy"
          />
        </section>
        <p
          dangerouslySetInnerHTML={{
            __html: i18n("product.refurbished.policy",
              `/help/detail/refurbished-products-policy?language=${encodeURIComponent(window.currentLang)}`),
          }}
          class="tw:mb-0 tw:pb-4"
        ></p>
      </main>
    </>
  );
};

const RefurbishedGradeCard = ({
  name,
  content,
  imageUrl,
  iconName,
}: {
  name: string;
  content: ReactElement;
  imageUrl: string;
  iconName: IconType;
}) => {
  return (
    <div class="tw:p-4 tw:border tw:border-fruugo-border-control tw:rounded-lg tw:w-full">
      <div class="tw:flex tw:justify-end">
        <span class="tw:px-2 tw:bg-fruugo-dark-teal tw:border-2 tw:border-fruugo-darkest-teal tw:text-white tw:rounded-full">
          {i18n("product.refurbished.example")}
        </span>
      </div>
      <img
        src={imageUrl}
        onError={(e) => handleImageError(e.target)}
        aria-hidden={true}
        class="tw:w-full tw:h-auto tw:max-w-sm tw:m-auto tw:block"
      />
      <h4 class="tw:flex tw:gap-2 tw:items-center">
        <Icon
          name={iconName}
          width={18}
          height={18}
          className="tw:w-5 tw:h-5"
        />
        <span>{name}</span>
      </h4>
      {content}
    </div>
  );
};

const ContentList = ({
  grade,
  children,
}: {
  grade: Grade;
  children: ReactNode;
}) => {
  return (
    <ul
      class={`tw:m-0 tw:p-0 tw:pl-4 ${readMoreListClass(grade)}`}
      data-item-class={`.${listItemClass(grade)}`}
    >
      {children}
    </ul>
  );
};

const FairGradeContent = () => {
  const itemClass = listItemClass("fair");
  return (
    <ContentList grade="fair">
      <li class={itemClass}>{i18n("product.refurbished.modal.fair.1")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.fair.2")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.fair.3")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.fair.4")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.fair.5")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.fair.6")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.fair.7")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.fair.8")}</li>
    </ContentList>
  );
};

const GoodGradeContent = () => {
  const itemClass = listItemClass("good");
  return (
    <ContentList grade="good">
      <li class={itemClass}>{i18n("product.refurbished.modal.good.1")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.good.2")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.good.3")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.good.4")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.good.5")}</li>
    </ContentList>
  );
};

const ExellentGradeContent = () => {
  const itemClass = listItemClass("excellent");
  return (
    <ContentList grade="excellent">
      <li class={itemClass}>{i18n("product.refurbished.modal.excellent.1")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.excellent.2")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.excellent.3")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.excellent.4")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.excellent.5")}</li>
    </ContentList>
  );
};

const PremiumGradeContent = () => {
  const itemClass = listItemClass("premium");
  return (
    <ContentList grade="premium">
      <li class={itemClass}>{i18n("product.refurbished.modal.premium.1")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.premium.2")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.premium.3")}</li>
      <li class={itemClass}>{i18n("product.refurbished.modal.premium.4")}</li>
    </ContentList>
  );
};
